import { setUIHCookie } from '@moonpig/web-shared-utils'
import { useStoreId } from '@moonpig/web-core-stores'
import { useCallback } from 'react'
import { useRouter } from '@moonpig/web-core-routing'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql/graphqlTypes'
import { useAllEppoExperiments } from '@moonpig/web-core-eppo/browser'
import { logger } from '@moonpig/web-core-monitoring'
import { useAnalyticsHandlers } from '@moonpig/launchpad-analytics'
import { sanitizeStrings } from '@moonpig/web-core-analytics'
import { useLaunchpadAnalyticsExperiment } from '../useLaunchpadAnalyticsExperiment'
import { createDynamicContent } from '../../analytics/utils'
import { leaveItBlankEvent, personaliseEvent } from '../events'
import { PageTypeEnum, ButtonActionName, ProductInfoProduct } from '../../types'
import { useHandleAddToBasket } from '../../utils/addToBasket'
import { useProductsLocaleText } from '../../locale'
import { useTracking } from '../../contexts/tracking'
import { useAddDigitalGiftWithCardToBasket } from '../../utils/addDigitalGiftToBasket/useAddDigitalGiftWithCardToBasket'
import { Journeys } from '../../utils/addDigitalGiftToBasket/Journeys'
import { useProductStore } from '../../contexts/product'
import { useBasket } from '../../contexts/basket'
import { useProductRequiresPersonalisationQuery } from '../../queries/useProductRequiresPersonalisationQuery'
import { isCardProduct } from '../../utils/isCardProduct'
import { E_CARD_SIZE_ID } from '../../constants'
import { createButtons } from './createButtons'
import { useAddDigitalGiftToBasket } from '../../utils/addDigitalGiftToBasket/useAddDigitalGiftToBasket'
import { useProductModal } from '../../contexts/productModal/Context'

type Props = {
  pageType: PageTypeEnum
  productType: string | undefined
  onTrackingInfo?: (event: { product: ProductInfoProduct }) => {
    label: string
    listName: string
    itemIndex: number
  }
}

const DIGITAL_GIFT_CATEGORIES = [
  DepartmentsEnum.DIGITAL_GIFTS,
  DepartmentsEnum.DIGITAL_GIFT_CARDS,
]

const isDigitalGift = (product: ProductInfoProduct) =>
  DIGITAL_GIFT_CATEGORIES.includes(product.category.department)

export const useCreateButtons = ({
  pageType,
  productType,
  onTrackingInfo,
}: Props) => {
  const [
    { product, variant, quantity, addon, isVariantSelected, productContext },
  ] = useProductStore()

  const [
    { digitalGift, lastLineItem, basketId, digitalGiftVariantSku },
    { setError, setSelectedAmountWarning },
  ] = useBasket()

  const { close: closeModal } = useProductModal()
  const storeId = useStoreId()
  const {
    personaliseProduct: trackPersonaliseProduct,
    selectContent,
    pageLocation,
    pageComponent,
  } = useTracking()
  const isLaunchpadAnalytics = useLaunchpadAnalyticsExperiment()

  const eppoExperiments = useAllEppoExperiments()

  const experiments = { ...eppoExperiments }

  const localiseProductsText = useProductsLocaleText()

  const router = useRouter()

  const { params } = router.getCurrentRoute<'search'>()
  const groupCardProjectId = params.groupCardProject
  const isCard = isCardProduct(product.category.department)
  const isEcard = variant.key === E_CARD_SIZE_ID

  const requiresEditing = useProductRequiresPersonalisationQuery({
    productId: product.id,
    skip: !isCard,
  })

  const { handleAddToBasket, addingToBasket } = useHandleAddToBasket({
    pageLocation: pageType,
    soldOutMessage: localiseProductsText('messaging.sold_out'),
    itsNotYouMessage: localiseProductsText('messaging.its_not_you'),
  })

  const { addDigitalGiftWithCardToBasket, loading: addingDigitalGiftToBasket } =
    useAddDigitalGiftWithCardToBasket()

  const { addDigitalGiftToBasket: addDigitalGiftToLastLineItem } =
    useAddDigitalGiftToBasket()

  const addProductToBasket = useCallback(
    async ({
      buttonActionName = ButtonActionName.ADD_TO_BASKET,
      redirectUser,
    }: {
      buttonActionName?: ButtonActionName
      redirectUser?: boolean
    }) => {
      setError(false)

      if (isVariantSelected === false) {
        setSelectedAmountWarning(true)
        return
      }

      const isAddToBasketAction =
        buttonActionName === ButtonActionName.ADD_TO_BASKET
      const shouldRedirectUser =
        redirectUser ?? !(productContext?.onAddToBasket && isAddToBasketAction)

      try {
        await handleAddToBasket({
          quantity,
          product,
          variant,
          filters: productContext?.filters || [],
          integerFilters: productContext?.integerFilters || [],
          experiments,
          searchTerm: params.q || '',
          selectedAddon: addon,
          itemIndex: product?.productIndex || 0,
          productsLength: productContext?.totalNumberOfProducts || 1,
          buttonActionName,
          requiresEditingValue: requiresEditing.value,
          componentName: 'product details',
          redirectUser: shouldRedirectUser,
          onTrackingInfo,
        })
        if (isAddToBasketAction) {
          productContext?.onAddToBasket?.({ product })
        }
      } catch (addToBasketError) {
        logger.fixToday('Failure adding product to basket', addToBasketError)
        setError(true)
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      experiments,
      handleAddToBasket,
      product,
      quantity,
      requiresEditing.value,
      variant,
    ],
    /* eslint-enable react-hooks/exhaustive-deps */
  )

  const addBasketCrossSellGiftToBasket = useCallback(async () => {
    await addProductToBasket({
      redirectUser: false,
    })
    if (isVariantSelected) closeModal()
  }, [addProductToBasket, closeModal, isVariantSelected])

  const addDigitalGiftWithCardToBasketCallback = useCallback(
    async (digitalGiftProduct: ProductInfoProduct) => {
      setUIHCookie({ product })
      setError(false)

      const selectedVariant =
        digitalGiftProduct.variants.find(
          v => v.sku?.toLowerCase() === digitalGiftVariantSku?.toLowerCase(),
        ) || digitalGiftProduct.masterVariant

      try {
        await addDigitalGiftWithCardToBasket({
          productId: digitalGiftProduct.id,
          variant: {
            ...selectedVariant,
            addons: [],
          },
          journey: Journeys.EXPERIENCE_GIFT_FIRST_UPGRADED,
          region: storeId,
          card: { id: product.id, sku: variant.sku },
        })
      } catch (e) {
        setError(true)
      }
    },
    [
      addDigitalGiftWithCardToBasket,
      product,
      variant,
      setError,
      storeId,
      digitalGiftVariantSku,
    ],
  )

  const addDigitalGiftToLastLineItemCallback = useCallback(async () => {
    try {
      if (!basketId) {
        logger.fixToday(
          'No basketId found when adding digital gift to last line item',
        )
        setError(true)
        return
      }
      if (!isVariantSelected) {
        setSelectedAmountWarning(true)
        return
      }

      await addDigitalGiftToLastLineItem({
        variant: {
          ...variant,
          addons: product.masterVariant.addons || null,
        },
        lastLineItem,
        basketId,
        region: storeId,
        department: product.category.department,
      })
      router.push({ name: 'basket', params: { region: storeId } })
    } catch (e) {
      logger.fixToday('Failed to add digital gift to basket', e)
      setError(true)
    }
  }, [
    addDigitalGiftToLastLineItem,
    setError,
    basketId,
    lastLineItem,
    storeId,
    product,
    router,
    variant,
    isVariantSelected,
    setSelectedAmountWarning,
  ])

  const createGroupCard = () => {
    const isGroupCardNew = groupCardProjectId === 'new'
    const routeName = isGroupCardNew
      ? 'group-cards-create-project'
      : 'group-cards-dashboard'
    const parts = isGroupCardNew ? [] : [groupCardProjectId || '']
    const action = groupCardProjectId
      ? 'select group card'
      : 'create group card'
    const label = groupCardProjectId ? 'next' : 'make group card'
    const show = groupCardProjectId ? undefined : 'create-modal'

    selectContent({
      action,
      component: 'button',
      label,
    })

    router.push({
      name: routeName,
      params: {
        productId: product.id,
        productVariantSku: variant.sku || /* istanbul ignore next */ '',
        region: storeId,
        show,
        parts,
      },
    })
  }

  const personaliseProduct = () => {
    trackPersonaliseProduct()
    addProductToBasket({ buttonActionName: ButtonActionName.PERSONALISE })
  }

  const personaliseAction = () => {
    setUIHCookie({ product, facets: productContext?.filters })
    addProductToBasket({ buttonActionName: ButtonActionName.PERSONALISE })
  }

  const buttonActions = {
    onAddToBasket: () => {
      setUIHCookie({ product, facets: productContext?.filters })
      addProductToBasket({})
    },
    addDigitalGiftToBasket: () => {
      if (digitalGift) {
        trackPersonaliseProduct()
        addDigitalGiftWithCardToBasketCallback(digitalGift)
      }
    },
    addCrossSellGiftToBasket: () => {
      if (isDigitalGift(product)) {
        addDigitalGiftToLastLineItemCallback()
        return
      }

      const addToBasketMethod = () => addProductToBasket({ redirectUser: true })

      return product.customisable ? personaliseProduct() : addToBasketMethod()
    },
    addBasketCrossSellGiftToBasket: () => {
      return product.customisable
        ? personaliseProduct()
        : addBasketCrossSellGiftToBasket()
    },
    personalise: () => {
      trackPersonaliseProduct()
      personaliseAction()
    },
    createGroupCard,
  }

  const variantTitle = sanitizeStrings(product.masterVariant.title) || 'n/a'
  const productId = product.id.toLowerCase()
  const componentName = pageComponent.toLowerCase()
  const personaliseLabel = `${productId} | ${variantTitle} | ${quantity || 'n/a'} | ${componentName}`
  const personaliseContentType = `product action | personalise | ${componentName}`
  const personaliseItemId = `${productId} | ${variantTitle}`
  const leaveItBlankLabel = `button | ${localiseProductsText('common.card_add_to_basket_cta')}`
  const leaveItBlankCategory = pageLocation.toLowerCase()
  const leaveItBlankEventLabel =
    `${leaveItBlankLabel} | ${productId}`.toLowerCase()
  const leaveItBlankContentType =
    `${pageLocation} | add to basket | ${leaveItBlankLabel}`
      .substring(0, 100)
      .toLowerCase()

  const personaliseClickEvent = personaliseEvent
    .withEventData({ label: personaliseLabel })
    .withContentData({
      content_type: personaliseContentType,
      item_id: personaliseItemId,
    })
    .withProductData({
      product_category: sanitizeStrings(product.category.name),
      product_id: productId,
      product_name: sanitizeStrings(product.title),
      product_price: product.masterVariant.price.centAmount / 100,
      product_quantity: quantity,
      product_variant: variantTitle,
      dynamic_content: createDynamicContent(product.dynamicImages ?? undefined),
    })

  const leaveItBlankClickEvent = leaveItBlankEvent
    .withEventData({
      category: leaveItBlankCategory,
      label: leaveItBlankEventLabel,
    })
    .withContentData({
      content_type: leaveItBlankContentType,
      item_id: productId,
    })

  const personaliseClickHandlers = useAnalyticsHandlers<HTMLButtonElement>(
    { onClick: personaliseClickEvent },
    { onClick: personaliseAction },
  )
  const leaveItBlankClickHandlers = useAnalyticsHandlers<HTMLButtonElement>(
    { onClick: leaveItBlankClickEvent },
    { onClick: buttonActions.onAddToBasket },
  )

  const buttons = createButtons({
    pageType,
    productType,
    product,
    groupCardProjectId,
    localiseProductsText,
    buttonActions,
    buttonHandlers: {
      personalise: isLaunchpadAnalytics
        ? personaliseClickHandlers
        : { onClick: buttonActions.personalise },
      leaveItBlank: isLaunchpadAnalytics
        ? leaveItBlankClickHandlers
        : { onClick: buttonActions.onAddToBasket },
    },
    showSecondaryButton:
      requiresEditing?.value === false && product.customisable,
    hasDigitalGift: !!digitalGift,
  })

  return {
    primaryButton: buttons.primaryButton,
    secondaryButton: buttons.secondaryButton,
    loading: addingToBasket || addingDigitalGiftToBasket,
    skipEditor:
      isCard && !groupCardProjectId && productType !== 'groupCard' && !isEcard,
  }
}
